import React from 'react';

/**
 * Round 4 (2026-10-30): legibility pass. The map was unreadable at its
 * rendered size (dark land on a dark panel, 14 Instagram pings inside an
 * 8px blob, 7px labels). Now: a faint blue sea under the land, lighter land,
 * a stronger coast, pings with a panel-colored outline and three distinct
 * styles per source (filled accent, filled light, hollow), the Instagram
 * cluster spread 1.6x about its centroid (still inland), four city labels,
 * and a taller full-width strip on phones. Geometry is unchanged.
 *
 * Round 3: rounds 1-2 used hand-authored waypoints smoothed into a single
 * Catmull-Rom curve, which read as a thick, doubled scribble rather than a
 * real coastline. This is now real geographic data: Natural Earth's public
 * domain 1:10m land + coastline datasets
 * (https://github.com/nvkelso/natural-earth-vector), clipped to
 * lon [-118.95, -116.85] / lat [32.50, 34.40], mainland polygon only
 * (Catalina etc. dropped), equirectangular-projected with a fixed
 * cos(33.5°) x-scale, and simplified with Douglas-Peucker at the render
 * scale (~0.5px tolerance). The viewBox IS that bbox projected 1:1 with
 * the render size, not a tighter crop of the content -- that's what gives
 * the Instagram cluster (and every other ping) real margin instead of
 * being jammed against an edge. All of this was computed offline with a
 * throwaway Python/Shapely script; only the resulting path strings are
 * committed here, not the raw datasets.
 *
 * It's drawn as an actual map now, not a line drawing: `LAND_PATH` is a
 * filled polygon (land, one step lighter than the panel), `COAST_PATH` is
 * the real coastline traced on top as a stroke (the ocean is just the
 * panel's own background showing through). Freeways are limited to I-5,
 * I-405 and I-10 as faint hairlines. It is decorative (`aria-hidden`) --
 * the report's own `role="img"` label already says everything a screen
 * reader needs.
 */

interface Ping {
  x: number;
  y: number;
  type: 'instagram' | 'google' | 'walkin';
}

export interface ServiceMapProps {
  /** True once the hero has entered view and the line-draw should run. */
  drawIn: boolean;
  /** How many of the 23 pings (in landing order) are currently visible. */
  pingsLanded: number;
  reduced: boolean;
}

const VIEWBOX = '0 0 217.1 235.6';

// Natural Earth 1:10m land polygon, clipped + projected + simplified (see
// file header). Closed ring -- fill only, no stroke (the coast edge below
// is what's visible as a line).
const LAND_PATH =
  'M188.59,230.70 L187.47,221.25 L186.14,217.46 L184.43,214.58 L182.15,212.95 L178.47,212.70 ' +
  'L178.25,211.15 L180.35,209.14 L181.89,209.10 L182.21,210.06 L184.26,210.95 L188.80,222.36 ' +
  'L189.73,223.17 L190.94,222.36 L191.05,220.66 L189.24,216.67 L189.33,214.44 L187.86,212.06 ' +
  'L183.30,208.94 L183.58,207.49 L179.93,207.66 L177.04,209.90 L177.17,214.57 L175.24,211.87 ' +
  'L175.00,200.35 L172.31,193.73 L173.32,192.06 L174.12,192.57 L174.76,191.70 L175.41,187.85 ' +
  'L173.22,174.41 L172.06,170.64 L170.81,169.15 L168.32,160.15 L164.21,153.02 L149.02,132.03 ' +
  'L139.97,125.18 L133.97,118.80 L125.61,113.49 L121.34,107.96 L116.37,103.95 L110.61,100.08 ' +
  'L105.45,98.22 L100.45,94.74 L88.87,82.96 L86.09,81.39 L82.45,79.51 L80.28,78.95 L79.05,79.67 ' +
  'L76.81,78.88 L78.94,81.66 L79.08,82.79 L78.29,82.87 L77.27,81.02 L76.54,81.56 L75.33,78.01 ' +
  'L71.91,78.69 L70.36,80.15 L69.61,79.78 L70.09,84.91 L69.03,84.79 L69.68,85.84 L68.11,86.40 ' +
  'L61.54,83.54 L60.77,82.37 L58.42,81.83 L56.73,82.39 L55.63,81.63 L54.02,77.66 L57.31,73.70 ' +
  'L57.76,69.94 L52.25,55.87 L48.32,49.72 L41.98,44.78 L29.31,44.77 L27.90,45.84 L20.85,45.65 ' +
  'L16.76,47.16 L14.79,49.67 L10.07,45.43 L0.00,43.80 L0.00,0.00 L217.14,0.00 L217.14,235.60 ' +
  'L188.83,235.60 L188.59,230.70 Z';

// Same source, the mainland edge only (an open path) -- this is the line
// that's actually visible as the "coast."
const COAST_PATH =
  'M0.00,43.80 L10.07,45.43 L14.79,49.67 L16.76,47.16 L20.85,45.65 L27.90,45.84 L29.31,44.77 ' +
  'L41.98,44.78 L48.32,49.72 L52.25,55.87 L57.76,69.94 L57.31,73.70 L54.02,77.66 L55.63,81.63 ' +
  'L56.73,82.39 L58.42,81.83 L60.77,82.37 L61.54,83.54 L68.11,86.40 L69.68,85.84 L69.03,84.79 ' +
  'L70.09,84.91 L69.61,79.78 L70.36,80.15 L71.91,78.69 L75.33,78.01 L76.54,81.56 L77.27,81.02 ' +
  'L78.29,82.87 L79.08,82.79 L78.94,81.66 L76.81,78.88 L79.05,79.67 L80.28,78.95 L82.45,79.51 ' +
  'L86.09,81.39 L88.87,82.96 L100.45,94.74 L105.45,98.22 L110.61,100.08 L116.37,103.95 ' +
  'L121.34,107.96 L125.61,113.49 L133.97,118.80 L139.97,125.18 L149.02,132.03 L164.21,153.02 ' +
  'L168.32,160.15 L170.81,169.15 L172.06,170.64 L175.41,187.85 L174.76,191.70 L174.12,192.57 ' +
  'L173.32,192.06 L172.31,193.73 L175.00,200.35 L175.24,211.87 L177.17,214.57 L177.04,209.90 ' +
  'L179.93,207.66 L183.58,207.49 L183.30,208.94 L187.86,212.06 L189.33,214.44 L189.24,216.67 ' +
  'L191.05,220.66 L190.94,222.36 L189.73,223.17 L188.80,222.36 L184.26,210.95 L182.21,210.06 ' +
  'L181.89,209.10 L180.35,209.14 L178.25,211.15 L178.47,212.70 L182.15,212.95 L184.43,214.58 ' +
  'L186.14,217.46 L187.47,221.25 L188.83,235.60';

// I-5 / I-405 / I-10 only, approximate real routes (interchange
// waypoints), same projection as the coast/land data above.
const FREEWAYS: { name: string; d: string }[] = [
  {
    name: 'I-5',
    d:
      'M198.43,230.27 L191.40,213.53 L180.85,184.64 L169.68,157.60 L138.35,120.65 ' +
      'L116.53,90.64 L111.98,81.10 L72.79,43.65 L63.59,31.62',
  },
  {
    name: 'I-405',
    d: 'M116.53,89.28 L86.86,68.20 L57.91,49.60 L53.25,44.02 L49.63,33.48',
  },
  {
    name: 'I-10',
    d: 'M47.44,47.18 L56.87,45.88 L72.79,45.51 L84.79,43.65 L101.33,42.16',
  },
];

// "los angeles" over land northwest of downtown; "san diego" beside the
// bay. Both are given a panel-colored halo in CSS (paint-order: stroke)
// so the freeway/coast lines never read as colliding with the text.
const LABELS: { text: string; x: number; y: number; anchor?: 'start' | 'end' }[] = [
  { text: 'Los Angeles', x: 58.0, y: 60.0 },
  { text: 'Long Beach', x: 76.0, y: 97.0, anchor: 'end' },
  { text: 'Orange County', x: 120.0, y: 101.0, anchor: 'start' },
  { text: 'San Diego', x: 169.0, y: 200.0, anchor: 'end' },
];

/**
 * 23 pings in landing order -- 14 "Instagram intro-offer ad", 6 Google, 3
 * walk-in / referral, matching `WEEKLY_REPORT_EXAMPLE.sources` exactly.
 * The 14 Instagram pings scatter irregularly over Pasadena / Eagle Rock
 * (~34.14N, -118.18W, the fictional Northside Pilates neighborhood),
 * fully inside the frame with real margin above them. Google and walk-in
 * are individually placed across LA (Santa Monica, DTLA, Long Beach,
 * Culver City, Sherman Oaks, Torrance, Burbank) with two down toward
 * Orange County / San Diego (Anaheim, La Jolla) -- every one of them
 * validated to land on the Natural Earth land polygon, never the ocean.
 */
const PINGS: Ping[] = [
  { x: 82.8, y: 27.1, type: 'instagram' },
  { x: 47.6, y: 47.1, type: 'google' },
  { x: 51.7, y: 31.0, type: 'walkin' },
  { x: 72.8, y: 43.6, type: 'google' },
  { x: 74.8, y: 35.9, type: 'instagram' },
  { x: 81.7, y: 75.6, type: 'google' },
  { x: 63.1, y: 69.4, type: 'walkin' },
  { x: 66.2, y: 27.3, type: 'walkin' },
  { x: 80.7, y: 26.0, type: 'instagram' },
  { x: 56.9, y: 47.1, type: 'google' },
  { x: 81.2, y: 36.9, type: 'instagram' },
  { x: 175.8, y: 194.7, type: 'google' },
  { x: 74.3, y: 32.2, type: 'instagram' },
  { x: 84.6, y: 32.1, type: 'instagram' },
  { x: 107.5, y: 69.4, type: 'google' },
  { x: 76.3, y: 33.5, type: 'instagram' },
  { x: 80.3, y: 35.7, type: 'instagram' },
  { x: 79.3, y: 29.8, type: 'instagram' },
  { x: 86.7, y: 32.2, type: 'instagram' },
  { x: 83.9, y: 36.1, type: 'instagram' },
  { x: 84.3, y: 31.3, type: 'instagram' },
  { x: 75.3, y: 32.1, type: 'instagram' },
  { x: 84.3, y: 33.5, type: 'instagram' },
];

const ServiceMap: React.FC<ServiceMapProps> = ({ drawIn, pingsLanded, reduced }) => {
  const lineClass = `wr__map-line${drawIn || reduced ? ' wr__map-line--drawn' : ''}`;

  return (
    <svg className="wr__map" viewBox={VIEWBOX} aria-hidden="true" preserveAspectRatio="xMidYMid meet">
      <rect className="wr__map-sea" x="0" y="0" width="217.1" height="235.6" />
      <path className="wr__map-land" d={LAND_PATH} />

      {FREEWAYS.map((fw) => (
        <path
          key={fw.name}
          className={`wr__map-freeway${drawIn || reduced ? ' wr__map-line--drawn' : ''}`}
          d={fw.d}
          pathLength={1}
        />
      ))}

      <path className={lineClass} d={COAST_PATH} pathLength={1} />

      {LABELS.map((label) => (
        <text
          key={label.text}
          className="wr__map-label"
          x={label.x}
          y={label.y}
          textAnchor={label.anchor ?? 'middle'}
        >
          {label.text}
        </text>
      ))}

      {PINGS.map((ping, i) => {
        if (i >= pingsLanded) return null;
        return (
          <g
            key={i}
            className={`wr__map-ping wr__map-ping--${ping.type}`}
            transform={`translate(${ping.x} ${ping.y})`}
          >
            {!reduced && <circle className="wr__map-ripple" r="2.6" />}
            <circle className="wr__map-dot" r="2.6" />
          </g>
        );
      })}
    </svg>
  );
};

export default React.memo(ServiceMap);
